import * as Yup from 'yup';
import { riskConsequencesNames, riskEventsNames, riskSourcesNames, Variable } from 'src/api/types';
import { ScenarioCsvHeaders } from 'src/api/types/misc';
import { ScenarioInput } from 'src/graphql/_generated';
import { convertToNumber, convertToString, convertDateToISO, isValidNumberValue } from 'src/utils/csv';
import { FormulaVariableInfo, parseConsequenceValue } from 'src/utils/formula';

/**
 * Maps for converting CSV field paths to human-readable column names in error messages.
 * Includes both ScenarioInput field names and CSV header names for flexibility.
 */
export const pathNames: Record<string, string> = {
  name: ScenarioCsvHeaders.Name,
  id: ScenarioCsvHeaders.ID,
  status: ScenarioCsvHeaders.Status,
  source: ScenarioCsvHeaders.Sources,
  event: ScenarioCsvHeaders.Events,
  consequence: ScenarioCsvHeaders.Consequences,
  riskOwner: ScenarioCsvHeaders.RiskOwner,
  riskNarrative: ScenarioCsvHeaders.RiskNarrative,
  frequencyTimes: ScenarioCsvHeaders.UnmodifiedFrequencyTimes,
  frequencyYears: ScenarioCsvHeaders.UnmodifiedFrequencyYears,
  frequencyNote: ScenarioCsvHeaders.FrequencyNote,
  lowerFormula: ScenarioCsvHeaders.UnmodifiedConsequenceLower,
  upperFormula: ScenarioCsvHeaders.UnmodifiedConsequenceUpper,
  consequenceNote: ScenarioCsvHeaders.ConsequenceNote,
  riskIdentifiedDate: ScenarioCsvHeaders.IdentifiedDate,
  reviewDate: ScenarioCsvHeaders.ReviewDate,
  managementStrategy: ScenarioCsvHeaders.RiskManagementStrategy,
  managementNote: ScenarioCsvHeaders.RiskManagementNote,
  // CSV header names -> themselves (for raw CSV validation)
  [ScenarioCsvHeaders.Name]: ScenarioCsvHeaders.Name,
  [ScenarioCsvHeaders.ID]: ScenarioCsvHeaders.ID,
  [ScenarioCsvHeaders.Status]: ScenarioCsvHeaders.Status,
  [ScenarioCsvHeaders.Sources]: ScenarioCsvHeaders.Sources,
  [ScenarioCsvHeaders.Events]: ScenarioCsvHeaders.Events,
  [ScenarioCsvHeaders.Consequences]: ScenarioCsvHeaders.Consequences,
  [ScenarioCsvHeaders.RiskOwner]: ScenarioCsvHeaders.RiskOwner,
  [ScenarioCsvHeaders.RiskNarrative]: ScenarioCsvHeaders.RiskNarrative,
  [ScenarioCsvHeaders.UnmodifiedFrequencyTimes]: ScenarioCsvHeaders.UnmodifiedFrequencyTimes,
  [ScenarioCsvHeaders.UnmodifiedFrequencyYears]: ScenarioCsvHeaders.UnmodifiedFrequencyYears,
  [ScenarioCsvHeaders.FrequencyNote]: ScenarioCsvHeaders.FrequencyNote,
  [ScenarioCsvHeaders.UnmodifiedConsequenceLower]: ScenarioCsvHeaders.UnmodifiedConsequenceLower,
  [ScenarioCsvHeaders.UnmodifiedConsequenceUpper]: ScenarioCsvHeaders.UnmodifiedConsequenceUpper,
  [ScenarioCsvHeaders.ConsequenceNote]: ScenarioCsvHeaders.ConsequenceNote,
  [ScenarioCsvHeaders.IdentifiedDate]: ScenarioCsvHeaders.IdentifiedDate,
  [ScenarioCsvHeaders.ReviewDate]: ScenarioCsvHeaders.ReviewDate,
  [ScenarioCsvHeaders.RiskManagementStrategy]: ScenarioCsvHeaders.RiskManagementStrategy,
  [ScenarioCsvHeaders.RiskManagementNote]: ScenarioCsvHeaders.RiskManagementNote,
};

/**
 * Maps a text value to its corresponding numeric enum value.
 * Returns null if no match, or an error string if value exists but doesn't match.
 */
const mapToNumberOrError = (
  list: Record<string, string>,
  value: string | null | undefined,
  errorFieldName: string,
): number | string | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const lowerCaseValue = String(value).toLowerCase().trim();
  const entry = Object.entries(list).find(([, name]) => name.toLowerCase() === lowerCaseValue);

  if (entry) {
    return parseInt(entry[0], 10);
  }
  return `Invalid ${errorFieldName}`;
};

export const mapSourceToNumber = (sourceText: string | null | undefined): number | string | null => {
  return mapToNumberOrError(riskSourcesNames, sourceText, 'Source');
};

export const mapEventToNumber = (eventText: string | null | undefined): number | string | null => {
  return mapToNumberOrError(riskEventsNames, eventText, 'Event');
};

export const mapConsequenceToNumber = (consequenceText: string | null | undefined): number | string | null => {
  return mapToNumberOrError(riskConsequencesNames, consequenceText, 'Consequence');
};

/**
 * Generates a downloadable CSV template link.
 */
export const generateCsvTemplateLink = (): string => {
  // Only include importable headers in the template
  const importableHeaders = [
    ScenarioCsvHeaders.Name,
    ScenarioCsvHeaders.ID,
    ScenarioCsvHeaders.Status,
    ScenarioCsvHeaders.Sources,
    ScenarioCsvHeaders.Events,
    ScenarioCsvHeaders.Consequences,
    ScenarioCsvHeaders.RiskOwner,
    ScenarioCsvHeaders.RiskNarrative,
    ScenarioCsvHeaders.UnmodifiedFrequencyTimes,
    ScenarioCsvHeaders.UnmodifiedFrequencyYears,
    ScenarioCsvHeaders.FrequencyNote,
    ScenarioCsvHeaders.UnmodifiedConsequenceLower,
    ScenarioCsvHeaders.UnmodifiedConsequenceUpper,
    ScenarioCsvHeaders.ConsequenceNote,
    ScenarioCsvHeaders.IdentifiedDate,
    ScenarioCsvHeaders.ReviewDate,
    ScenarioCsvHeaders.RiskManagementStrategy,
    ScenarioCsvHeaders.RiskManagementNote,
  ];

  const csvString = importableHeaders.join(',') + '\n';
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  return URL.createObjectURL(blob);
};

export interface ScenarioCsvRow {
  [ScenarioCsvHeaders.Name]?: string | null;
  [ScenarioCsvHeaders.ID]?: string | null;
  [ScenarioCsvHeaders.Status]?: string | null;
  [ScenarioCsvHeaders.Sources]?: string | null;
  [ScenarioCsvHeaders.Events]?: string | null;
  [ScenarioCsvHeaders.Consequences]?: string | null;
  [ScenarioCsvHeaders.RiskOwner]?: string | null;
  [ScenarioCsvHeaders.RiskNarrative]?: string | null;
  [ScenarioCsvHeaders.UnmodifiedFrequencyTimes]?: string | number | null;
  [ScenarioCsvHeaders.UnmodifiedFrequencyYears]?: string | number | null;
  [ScenarioCsvHeaders.FrequencyNote]?: string | null;
  [ScenarioCsvHeaders.UnmodifiedConsequenceLower]?: string | number | null;
  [ScenarioCsvHeaders.UnmodifiedConsequenceUpper]?: string | number | null;
  [ScenarioCsvHeaders.ConsequenceNote]?: string | null;
  [ScenarioCsvHeaders.IdentifiedDate]?: string | null;
  [ScenarioCsvHeaders.ReviewDate]?: string | null;
  [ScenarioCsvHeaders.RiskManagementStrategy]?: string | null;
  [ScenarioCsvHeaders.RiskManagementNote]?: string | null;
}

export interface MappedScenarioResult {
  scenario: ScenarioInput;
  missingVariables: string[];
  foundVariables: FormulaVariableInfo[];
  mappingErrors: { field: string; error: string }[];
}

/**
 * Maps a CSV row to a ScenarioInput object, converting variable names to IDs in formulas.
 */
export const mapCsvRowToScenario = (row: ScenarioCsvRow, variables: Variable[]): MappedScenarioResult => {
  const mappingErrors: { field: string; error: string }[] = [];
  const missingVariables: string[] = [];
  const foundVariables: FormulaVariableInfo[] = [];

  // Map source/event/consequence text to numbers
  const mappedSource = mapSourceToNumber(row[ScenarioCsvHeaders.Sources]);
  const mappedEvent = mapEventToNumber(row[ScenarioCsvHeaders.Events]);
  const mappedConsequence = mapConsequenceToNumber(row[ScenarioCsvHeaders.Consequences]);

  // Check for mapping errors
  if (typeof mappedSource === 'string') {
    mappingErrors.push({ field: 'source', error: mappedSource });
  }
  if (typeof mappedEvent === 'string') {
    mappingErrors.push({ field: 'event', error: mappedEvent });
  }
  if (typeof mappedConsequence === 'string') {
    mappingErrors.push({ field: 'consequence', error: mappedConsequence });
  }

  // Parse consequence formulas (could be numbers or formulas with variable names)
  const lowerResult = parseConsequenceValue(row[ScenarioCsvHeaders.UnmodifiedConsequenceLower], variables);
  const upperResult = parseConsequenceValue(row[ScenarioCsvHeaders.UnmodifiedConsequenceUpper], variables);

  // Collect missing variables and found variables
  missingVariables.push(...lowerResult.missingVariables, ...upperResult.missingVariables);
  foundVariables.push(...lowerResult.foundVariables, ...upperResult.foundVariables);

  const rawId = row[ScenarioCsvHeaders.ID];
  const id = rawId ? String(rawId).trim() : undefined;

  const scenario: ScenarioInput = {
    id: id || undefined,
    name: convertToString(row[ScenarioCsvHeaders.Name]) || '',
    ...(row[ScenarioCsvHeaders.Status] && { status: convertToString(row[ScenarioCsvHeaders.Status]) }),
    source: typeof mappedSource === 'number' ? mappedSource : undefined,
    event: typeof mappedEvent === 'number' ? mappedEvent : undefined,
    consequence: typeof mappedConsequence === 'number' ? mappedConsequence : undefined,
    ...(row[ScenarioCsvHeaders.RiskOwner] && {
      riskOwner: { text: convertToString(row[ScenarioCsvHeaders.RiskOwner]) || '' },
    }),
    ...(row[ScenarioCsvHeaders.RiskNarrative] && {
      riskNarrative: convertToString(row[ScenarioCsvHeaders.RiskNarrative]),
    }),
    frequencyTimes: convertToNumber(row[ScenarioCsvHeaders.UnmodifiedFrequencyTimes]),
    frequencyYears: convertToNumber(row[ScenarioCsvHeaders.UnmodifiedFrequencyYears]),
    ...(row[ScenarioCsvHeaders.FrequencyNote] && {
      frequencyNote: convertToString(row[ScenarioCsvHeaders.FrequencyNote]),
    }),
    lowerFormula: lowerResult.formula || undefined,
    upperFormula: upperResult.formula || undefined,
    ...(row[ScenarioCsvHeaders.ConsequenceNote] && {
      consequenceNote: convertToString(row[ScenarioCsvHeaders.ConsequenceNote]),
    }),
    ...(row[ScenarioCsvHeaders.IdentifiedDate] && {
      riskIdentifiedDate: convertDateToISO(row[ScenarioCsvHeaders.IdentifiedDate]),
    }),
    ...(row[ScenarioCsvHeaders.ReviewDate] && {
      reviewDate: convertDateToISO(row[ScenarioCsvHeaders.ReviewDate]),
    }),
    ...(row[ScenarioCsvHeaders.RiskManagementStrategy] && {
      managementStrategy: convertToString(row[ScenarioCsvHeaders.RiskManagementStrategy]),
    }),
    ...(row[ScenarioCsvHeaders.RiskManagementNote] && {
      managementNote: convertToString(row[ScenarioCsvHeaders.RiskManagementNote]),
    }),
  };

  // Deduplicate foundVariables by snakeName
  const uniqueFoundVariables = foundVariables.filter(
    (v, i, arr) => arr.findIndex((x) => x.snakeName === v.snakeName) === i,
  );

  return { scenario, missingVariables, foundVariables: uniqueFoundVariables, mappingErrors };
};

export const scenarioCsvValidationSchema = Yup.object({
  [ScenarioCsvHeaders.Name]: Yup.string().required('Name is required'),
  [ScenarioCsvHeaders.ID]: Yup.string().nullable(),
  [ScenarioCsvHeaders.Status]: Yup.string().nullable(),
  [ScenarioCsvHeaders.Sources]: Yup.string().nullable(),
  [ScenarioCsvHeaders.Events]: Yup.string().nullable(),
  [ScenarioCsvHeaders.Consequences]: Yup.string().nullable(),
  [ScenarioCsvHeaders.RiskOwner]: Yup.string().nullable(),
  [ScenarioCsvHeaders.RiskNarrative]: Yup.string().nullable(),
  [ScenarioCsvHeaders.UnmodifiedFrequencyTimes]: Yup.mixed().test(
    'is-valid-number',
    'Frequency Times must be a valid number',
    (value) => {
      const isValid = isValidNumberValue(value);
      console.log(`[CSV Validation] ${ScenarioCsvHeaders.UnmodifiedFrequencyTimes}: "${value}" -> isValid: ${isValid}`);
      return isValid;
    },
  ),
  [ScenarioCsvHeaders.UnmodifiedFrequencyYears]: Yup.mixed().test(
    'is-valid-number',
    'Frequency Years must be a valid number',
    (value) => {
      const isValid = isValidNumberValue(value);
      console.log(`[CSV Validation] ${ScenarioCsvHeaders.UnmodifiedFrequencyYears}: "${value}" -> isValid: ${isValid}`);
      return isValid;
    },
  ),
  [ScenarioCsvHeaders.FrequencyNote]: Yup.string().nullable(),
  [ScenarioCsvHeaders.UnmodifiedConsequenceLower]: Yup.string().nullable(),
  [ScenarioCsvHeaders.UnmodifiedConsequenceUpper]: Yup.string().nullable(),
  [ScenarioCsvHeaders.ConsequenceNote]: Yup.string().nullable(),
  [ScenarioCsvHeaders.IdentifiedDate]: Yup.string().nullable(),
  [ScenarioCsvHeaders.ReviewDate]: Yup.string().nullable(),
  [ScenarioCsvHeaders.RiskManagementStrategy]: Yup.string().nullable(),
  [ScenarioCsvHeaders.RiskManagementNote]: Yup.string().nullable(),
});
